import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Container, Typography, Box, Avatar, Grid, Skeleton, Button, Chip, Stack,
    Link as MuiLink,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { fetchAuthSession } from 'aws-amplify/auth';
import { optionalAuthFetch, checkUserGroup } from '../utils/authUtils';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import GavelIcon from '@mui/icons-material/Gavel';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PercentIcon from '@mui/icons-material/Percent';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PaidIcon from '@mui/icons-material/Paid';
import CircleIcon from '@mui/icons-material/Circle';
import LockIcon from '@mui/icons-material/Lock';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TimelineIcon from '@mui/icons-material/Timeline';
import BarChartIcon from '@mui/icons-material/BarChart';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { ResponsiveBar } from '@nivo/bar';
import { Surface, SectionHeader, StatCard } from '../components/primitives';
import { getInfoEdge } from '../utils/infoEdge';

const EDGE_TONE = { strong: 'positive', likely: 'positive', possible: 'default', none: 'default', insufficient: 'default' };

const LEGISLATION_PAGE_SIZE = 5;

/** Congress.gov API type segment (lowercase) → www.congress.gov bill path segment */
const BILL_TYPE_SLUG = {
    hr: 'house-bill',
    s: 'senate-bill',
    hjres: 'house-joint-resolution',
    sjres: 'senate-joint-resolution',
    hconres: 'house-concurrent-resolution',
    sconres: 'senate-concurrent-resolution',
    hres: 'house-resolution',
    sres: 'senate-resolution',
    hjr: 'house-joint-resolution',
    sjr: 'senate-joint-resolution',
};

function congressOrdinal(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) return String(n);
    const j = x % 10;
    const k = x % 100;
    if (j === 1 && k !== 11) return `${x}st`;
    if (j === 2 && k !== 12) return `${x}nd`;
    if (j === 3 && k !== 13) return `${x}rd`;
    return `${x}th`;
}

/**
 * Public bill page on www.congress.gov. API `bill.url` targets api.congress.gov and
 * requires an api_key, so it is unsuitable for "View Bill" in the browser.
 */
function publicCongressBillUrl(bill) {
    if (!bill || typeof bill !== 'object') return null;
    let congress = bill.congress;
    let typeCode = bill.type != null ? String(bill.type).toLowerCase() : '';
    let number = bill.number != null ? String(bill.number) : '';

    const apiUrl = bill.url;
    if ((congress == null || !typeCode || !number) && typeof apiUrl === 'string') {
        const m = apiUrl.match(/\/v3\/bill\/(\d+)\/([a-z]+)\/([^/?]+)/i);
        if (m) {
            congress = parseInt(m[1], 10);
            typeCode = m[2].toLowerCase();
            number = m[3];
        }
    }
    const slug = BILL_TYPE_SLUG[typeCode];
    if (congress == null || !slug || !number) return null;
    const num = String(number).replace(/^0+(?=\d)/, '') || number;
    return `https://www.congress.gov/bill/${congressOrdinal(congress)}-congress/${slug}/${num}`;
}

// ── Loading skeleton ─────────────────────────────────────────────────────────

function LoadingSkeleton() {
    return (
        <Container sx={{ pt: 6, pb: 8 }}>
            <Skeleton width={160} height={32} sx={{ mb: 3 }} />
            <Surface padding={{ xs: 2.5, sm: 4 }} sx={{ mb: 4 }}>
                <Box display="flex" alignItems="center" gap={3}>
                    <Skeleton variant="circular" width={112} height={112} sx={{ flexShrink: 0 }} />
                    <Box flex={1}>
                        <Skeleton width="55%" height={36} />
                        <Skeleton width="40%" height={20} sx={{ mt: 1 }} />
                        <Skeleton width="50%" height={16} sx={{ mt: 0.5 }} />
                    </Box>
                </Box>
            </Surface>
            <Grid container spacing={2} sx={{ mb: 4 }}>
                {[...Array(4)].map((_, i) => (
                    <Grid item xs={6} md={3} key={i}>
                        <Skeleton variant="rectangular" height={92} sx={{ borderRadius: 1.5 }} />
                    </Grid>
                ))}
            </Grid>
            <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 1.5, mb: 4 }} />
            <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 1.5 }} />
        </Container>
    );
}

// ── Main page ────────────────────────────────────────────────────────────────

function CongressMemberProfile() {
    const { memberId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();

    const [memberData, setMemberData] = useState(null);
    const [legislationData, setLegislationData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPremium, setIsPremium] = useState(false);
    const [isTracked, setIsTracked] = useState(false);
    const [trackLoading, setTrackLoading] = useState(false);
    const [showAllLegislation, setShowAllLegislation] = useState(false);
    const [tradesData, setTradesData] = useState([]);
    const [tradesLoading, setTradesLoading] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { isPremium: premium } = await checkUserGroup();
            setIsPremium(premium);

            // Member profile + legislation are public; watchlist lookup is Pro-only
            // and requires a valid session.
            let trackReq = Promise.resolve(null);
            if (premium) {
                try {
                    const session = await fetchAuthSession();
                    const idToken = session?.tokens?.idToken?.toString();
                    if (idToken) {
                        trackReq = fetch('https://api.capitolgains.io/user/track-member', {
                            headers: { Authorization: `Bearer ${idToken}` },
                        });
                    }
                } catch {
                    trackReq = Promise.resolve(null);
                }
            }

            const [memberRes, legislationRes, trackRes] = await Promise.allSettled([
                optionalAuthFetch('https://api.capitolgains.io/member', {
                    method: 'POST',
                    body: { bioguideId: memberId },
                }),
                optionalAuthFetch('https://api.capitolgains.io/member/legislation', {
                    method: 'POST',
                    body: { bioguideId: memberId },
                }),
                trackReq,
            ]);

            if (memberRes.status === 'fulfilled' && memberRes.value.ok) {
                const data = await memberRes.value.json();
                setMemberData(data);
            } else {
                let msg = 'Failed to load member profile';
                if (memberRes.status === 'fulfilled' && memberRes.value) {
                    const res = memberRes.value;
                    try {
                        const errBody = await res.json();
                        if (errBody?.message) msg = errBody.message;
                    } catch {
                        msg = res.status ? `Request failed (${res.status})` : msg;
                    }
                    if (res.status === 404) {
                        msg = 'No profile on file for this member ID. They may not appear in our trader rankings export yet, or their disclosure name did not match Congress.gov when stats were built.';
                    } else if (res.status >= 500) {
                        msg = 'Server error while loading this profile. Try again in a moment.';
                    }
                } else if (memberRes.status === 'rejected') {
                    msg = memberRes.reason?.message || msg;
                }
                throw new Error(msg);
            }

            if (legislationRes.status === 'fulfilled' && legislationRes.value.ok) {
                const legislation = await legislationRes.value.json();
                setLegislationData(Array.isArray(legislation) ? legislation : []);
            }

            if (trackRes.status === 'fulfilled' && trackRes.value?.ok) {
                const trackData = await trackRes.value.json();
                setIsTracked((trackData.tracked_members || []).includes(memberId));
            }
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [memberId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Trade history — fetched once member name resolves
    useEffect(() => {
        if (!memberData) return;
        const firstName = memberData.member_details?.additional_details?.firstName;
        const lastName = memberData.member_details?.additional_details?.lastName;
        if (!firstName || !lastName) return;

        const fetchTrades = async () => {
            setTradesLoading(true);
            try {
                const res = await optionalAuthFetch(
                    'https://api.capitolgains.io/user/trades',
                    { method: 'POST', body: { first_name: firstName, last_name: lastName } }
                );
                if (res.ok) {
                    const json = await res.json();
                    setTradesData(json.disclosures || []);
                }
            } catch (err) {
                console.error('Error fetching trade history:', err);
            } finally {
                setTradesLoading(false);
            }
        };
        fetchTrades();
    }, [memberData]);

    const chartData = useMemo(() => {
        const map = {};
        tradesData.forEach(trade => {
            const d = new Date(trade.transaction_date);
            const sortKey = trade.transaction_date.slice(0, 7);
            const label = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
            if (!map[sortKey]) map[sortKey] = { month: label, sortKey, Purchases: 0, Sales: 0 };
            if (trade.transaction === 'purchase') map[sortKey].Purchases++;
            else map[sortKey].Sales++;
        });
        return Object.values(map).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    }, [tradesData]);

    const topTickers = useMemo(() => {
        const counts = {};
        tradesData.forEach(trade => {
            if (trade.ticker) counts[trade.ticker] = (counts[trade.ticker] || 0) + 1;
        });
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 12)
            .map(([ticker, count]) => ({ ticker, count }));
    }, [tradesData]);

    // Collapse consecutive re-elections in the same chamber into a single tenure
    const groupedTerms = useMemo(() => {
        const rawTerms = memberData?.member_details?.additional_details?.terms;
        if (!Array.isArray(rawTerms) || rawTerms.length === 0) return [];
        const sorted = [...rawTerms].sort((a, b) => (a.startYear || 0) - (b.startYear || 0));
        const groups = [];
        for (const term of sorted) {
            const last = groups[groups.length - 1];
            const canMerge =
                last &&
                last.memberType === term.memberType &&
                last.chamber === term.chamber &&
                (last.endYear == null || Math.abs((term.startYear || 0) - last.endYear) <= 2);
            if (canMerge) {
                if (term.endYear == null) last.endYear = null;
                else if (last.endYear != null) last.endYear = Math.max(last.endYear, term.endYear);
            } else {
                groups.push({ ...term });
            }
        }
        return groups;
    }, [memberData]);

    const handleTrackClick = async () => {
        setTrackLoading(true);
        try {
            const session = await fetchAuthSession();
            const idToken = session.tokens.idToken.toString();
            const response = await fetch('https://api.capitolgains.io/user/track-member', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
                body: JSON.stringify({ members: [memberId] }),
            });
            if (!response.ok) throw new Error('Failed to update tracking');
            setIsTracked(prev => !prev);
        } catch (err) {
            console.error('Error updating tracking:', err);
        } finally {
            setTrackLoading(false);
        }
    };

    if (loading) return <LoadingSkeleton />;

    if (error) return (
        <Container sx={{ pt: 6, pb: 8 }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{ mb: 3, color: 'text.secondary' }}
            >
                Back
            </Button>
            <Surface padding={4} sx={{ textAlign: 'center' }}>
                <Typography variant="h5" sx={{ color: 'error.main', mb: 1 }}>
                    Failed to load member profile
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                    {error.message}
                </Typography>
                <Button variant="contained" onClick={fetchData}>Try Again</Button>
            </Surface>
        </Container>
    );

    if (!memberData) return null;

    const {
        name = '',
        member_details = {},
        avg_returns = {},
        bullish_transactions = {},
        bearish_transactions = {},
        committees = { committees: [], subcommittees: [] },
    } = memberData;

    const { additional_details = {}, party = '', state = '', image, website } = member_details;
    const {
        birthYear,
        addressInformation = {},
        cosponsoredLegislation = {},
        sponsoredLegislation = {},
    } = additional_details;

    const purchaseROI = avg_returns?.stocks?.purchase?.roi ?? 0;
    const roiPercent = ((purchaseROI - 1) * 100).toFixed(1);
    const roiPositive = purchaseROI >= 1;
    const accuracy = bullish_transactions?.accuracy ?? 0;
    const purchaseCount = bullish_transactions?.count ?? 0;
    const profitEstimated = bullish_transactions?.profit_estimated ?? 0;
    const alpha = bullish_transactions?.alpha ?? null;
    const alphaPercent = alpha ? (alpha * 100).toFixed(1) : null;
    const memberGrade = memberData?.grade ?? null;
    // P(true alpha > 0) under the empirical-Bayes posterior, 0-1.
    const alphaProb = memberData?.alpha_prob_positive ?? null;
    const edge = getInfoEdge(alphaProb, purchaseCount);
    const infoEdge = { label: edge.label, tone: EDGE_TONE[edge.key] };

    const formattedProfit = profitEstimated
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(profitEstimated)
        : '—';

    const bearishRoiPercent = bearish_transactions?.roi
        ? ((bearish_transactions.roi - 1) * 100).toFixed(1)
        : null;

    const partyKey = party === 'Democratic' ? 'democrat' : party === 'Republican' ? 'republican' : 'independent';
    const partyColor = theme.palette.custom.party[partyKey];

    const statCards = [
        {
            icon: ShowChartIcon,
            label: 'Information Edge',
            value: `${memberGrade ? memberGrade + ' · ' : ''}${infoEdge.label}`,
            tone: infoEdge.tone,
            tooltip: 'Statistical likelihood that this member is trading with an information advantage not available to the general public. Based on bootstrap confidence interval analysis of their full trading history.',
        },
        ...(alphaPercent ? [{
            icon: ShowChartIcon,
            label: 'Alpha vs S&P 500',
            value: `${parseFloat(alphaPercent) >= 0 ? '+' : ''}${alphaPercent}%`,
            tone: parseFloat(alphaPercent) >= 0 ? 'positive' : 'negative',
            tooltip: 'Average excess return over the S&P 500 on stock purchases. Positive alpha means this trader consistently beats the market.',
            mono: true,
        }] : []),
        {
            icon: ShowChartIcon,
            label: 'Annual Return',
            value: `${roiPositive ? '+' : ''}${roiPercent}%`,
            tone: roiPositive ? 'positive' : 'negative',
            tooltip: 'Average annual percentage return on stock purchases.',
        },
        {
            icon: PercentIcon,
            label: 'Hit Rate',
            value: `${(accuracy * 100).toFixed(1)}%`,
            tone: 'default',
            tooltip: 'Percentage of purchases that were profitable after 1 year.',
        },
        {
            icon: ShoppingCartIcon,
            label: 'Purchases',
            value: purchaseCount.toLocaleString(),
            tone: 'default',
            tooltip: 'Total number of stock purchases disclosed.',
        },
        {
            icon: PaidIcon,
            label: 'Est. Profit',
            value: formattedProfit,
            tone: 'default',
            tooltip: 'Estimated profit assuming all assets sold exactly one year after purchase.',
        },
    ];

    const visibleLegislation = showAllLegislation
        ? legislationData
        : legislationData.slice(0, LEGISLATION_PAGE_SIZE);

    return (
        <Container sx={{ pt: 6, pb: 8 }}>

            {/* Back button */}
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{
                    mb: 3,
                    color: 'text.secondary',
                    pl: 1,
                    '&:hover': { color: 'text.primary', bgcolor: 'rgba(255,255,255,0.03)' },
                }}
            >
                Back to Leaderboard
            </Button>

            {/* ── Hero header ── */}
            <Surface variant="accent" padding={{ xs: 2.5, sm: 4 }} sx={{ mb: 4 }}>
                <Box
                    display="flex"
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'center', sm: 'flex-start' }}
                    gap={3}
                >
                    <Avatar
                        src={image}
                        alt={name}
                        sx={{
                            width: { xs: 96, sm: 112 },
                            height: { xs: 96, sm: 112 },
                            border: `2px solid ${partyColor}`,
                            boxShadow: `0 0 16px ${partyColor}40`,
                            flexShrink: 0,
                        }}
                    />

                    <Box flex={1} textAlign={{ xs: 'center', sm: 'left' }}>
                        <Typography
                            variant="h1"
                            sx={{ color: 'text.primary', mb: 0.5, fontSize: { xs: '1.5rem', sm: '2rem' } }}
                        >
                            {name}
                        </Typography>
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            justifyContent={{ xs: 'center', sm: 'flex-start' }}
                            mb={0.75}
                        >
                            <CircleIcon sx={{ fontSize: 8, color: partyColor }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {party} · {state}{birthYear ? ` · b. ${birthYear}` : ''}
                            </Typography>
                        </Box>

                        {addressInformation.officeAddress && (
                            <Typography variant="caption" sx={{ color: 'text.muted', display: 'block' }}>
                                {addressInformation.officeAddress}
                                {addressInformation.phoneNumber ? ` · ${addressInformation.phoneNumber}` : ''}
                            </Typography>
                        )}

                        {website && (
                            <MuiLink
                                href={website}
                                target="_blank"
                                rel="noreferrer"
                                sx={{ fontSize: '0.8125rem', display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}
                            >
                                Official Website <OpenInNewIcon sx={{ fontSize: 13 }} />
                            </MuiLink>
                        )}
                    </Box>

                    {/* Track / lock button */}
                    <Box sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
                        {isPremium ? (
                            <Button
                                variant={isTracked ? 'outlined' : 'contained'}
                                onClick={handleTrackClick}
                                disabled={trackLoading}
                                sx={{ width: { xs: '100%', sm: 180 } }}
                            >
                                {trackLoading
                                    ? 'Updating…'
                                    : isTracked ? 'Remove from Watchlist' : 'Add to Watchlist'}
                            </Button>
                        ) : (
                            <Button
                                variant="outlined"
                                component={Link}
                                to="/pricing"
                                startIcon={<LockIcon sx={{ fontSize: 14 }} />}
                                sx={{ width: { xs: '100%', sm: 180 } }}
                            >
                                Track Member
                            </Button>
                        )}
                    </Box>
                </Box>
            </Surface>

            {/* ── Committee Assignments ── */}
            {(committees.committees?.length > 0 || committees.subcommittees?.length > 0) && (
                <>
                    <SectionHeader icon={AccountBalanceIcon}>Committee Assignments</SectionHeader>
                    <Surface sx={{ mb: 4 }}>
                        {committees.committees?.length > 0 && (
                            <Box sx={{ mb: committees.subcommittees?.length > 0 ? 2.5 : 0 }}>
                                <Typography
                                    variant="overline"
                                    sx={{ color: 'text.muted', display: 'block', mb: 1 }}
                                >
                                    Committees
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {committees.committees.map(c => (
                                        <Chip
                                            key={c.code}
                                            label={c.name}
                                            size="small"
                                            sx={{
                                                bgcolor: 'custom.surface.elevated',
                                                color: 'text.primary',
                                                border: t => `1px solid ${t.palette.custom.border.subtle}`,
                                                fontWeight: 500,
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )}
                        {committees.subcommittees?.length > 0 && (
                            <Box>
                                <Typography
                                    variant="overline"
                                    sx={{ color: 'text.muted', display: 'block', mb: 1 }}
                                >
                                    Subcommittees
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {committees.subcommittees.map(s => (
                                        <Chip
                                            key={s.code}
                                            label={s.name}
                                            size="small"
                                            sx={{
                                                bgcolor: 'transparent',
                                                color: 'text.secondary',
                                                border: t => `1px solid ${t.palette.custom.border.subtle}`,
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Surface>
                </>
            )}

            {/* ── Trading Performance ── */}
            <SectionHeader icon={TrendingUpIcon}>Trading Performance</SectionHeader>
            <Grid container spacing={2} sx={{ mb: 4 }}>
                {statCards.map(stat => (
                    <Grid item xs={6} md={3} key={stat.label}>
                        <StatCard
                            icon={stat.icon}
                            label={stat.label}
                            value={stat.value}
                            tone={stat.tone}
                            tooltip={stat.tooltip}
                            mono
                        />
                    </Grid>
                ))}
            </Grid>

            {/* ── Trading Activity ── */}
            {(tradesLoading || chartData.length > 0) && (
                <>
                    <SectionHeader icon={TimelineIcon}>Trading Activity</SectionHeader>
                    <Surface sx={{ mb: 4 }}>
                        {tradesLoading ? (
                            <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />
                        ) : (
                            <>
                                <Box sx={{ height: 280 }}>
                                    <ResponsiveBar
                                        data={chartData}
                                        keys={['Purchases', 'Sales']}
                                        indexBy="month"
                                        margin={{ top: 10, right: 100, bottom: 52, left: 38 }}
                                        padding={0.45}
                                        innerPadding={2}
                                        groupMode="grouped"
                                        colors={[
                                            theme.palette.custom.direction.buyFg,
                                            theme.palette.custom.direction.sellFg,
                                        ]}
                                        borderRadius={2}
                                        enableLabel={false}
                                        axisBottom={{
                                            tickRotation: -30,
                                            tickValues: (() => {
                                                const step = Math.max(1, Math.ceil(chartData.length / 10));
                                                return chartData.filter((_, i) => i % step === 0).map(d => d.month);
                                            })(),
                                        }}
                                        axisLeft={{
                                            tickValues: 4,
                                            format: v => Number.isInteger(v) ? v : '',
                                        }}
                                        gridYValues={4}
                                        tooltip={({ id, value, indexValue }) => (
                                            <Box sx={{
                                                bgcolor: 'custom.surface.elevated',
                                                p: 1.25,
                                                borderRadius: 1,
                                                border: theme => `1px solid ${theme.palette.custom.border.default}`,
                                                boxShadow: theme => theme.customShadows.md,
                                            }}>
                                                <Typography variant="caption" sx={{ color: 'text.muted', display: 'block' }}>
                                                    {indexValue}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: id === 'Purchases'
                                                            ? theme.palette.custom.direction.buyFg
                                                            : theme.palette.custom.direction.sellFg,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {id}: {value}
                                                </Typography>
                                            </Box>
                                        )}
                                        legends={[{
                                            dataFrom: 'keys',
                                            anchor: 'top-right',
                                            direction: 'column',
                                            translateX: 100,
                                            itemWidth: 90,
                                            itemHeight: 20,
                                            itemsSpacing: 4,
                                            symbolShape: 'square',
                                            symbolSize: 10,
                                            itemTextColor: theme.palette.text.secondary,
                                        }]}
                                        theme={{
                                            axis: {
                                                domain: { line: { stroke: theme.palette.custom.border.subtle } },
                                                ticks: {
                                                    line: { stroke: theme.palette.custom.border.subtle },
                                                    text: { fill: theme.palette.text.secondary, fontSize: 11, fontFamily: theme.typography.fontFamily },
                                                },
                                            },
                                            grid: { line: { stroke: theme.palette.custom.border.subtle } },
                                            legends: { text: { fill: theme.palette.text.secondary } },
                                        }}
                                    />
                                </Box>

                                {topTickers.length > 0 && (
                                    <Box mt={2}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                                            <BarChartIcon sx={{ fontSize: 14, color: 'text.muted' }} />
                                            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                                                Most Traded
                                            </Typography>
                                        </Box>
                                        <Box display="flex" flexWrap="wrap" gap={0.75}>
                                            {topTickers.map(({ ticker, count }) => (
                                                <Chip
                                                    key={ticker}
                                                    label={`${ticker}  ×${count}`}
                                                    size="small"
                                                    component={Link}
                                                    to={`/issuers/${encodeURIComponent(ticker)}`}
                                                    clickable
                                                    sx={{
                                                        bgcolor: 'rgba(255,255,255,0.04)',
                                                        color: 'text.primary',
                                                        border: theme => `1px solid ${theme.palette.custom.border.subtle}`,
                                                        fontFamily: theme => theme.typography.fontFamilyMono,
                                                        fontSize: '0.72rem',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            bgcolor: 'custom.surface.elevated',
                                                            borderColor: theme => theme.palette.custom.border.default,
                                                        },
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </>
                        )}
                    </Surface>
                </>
            )}

            {/* ── Recent Trades ── */}
            {tradesData.length > 0 && (
                <>
                    <SectionHeader icon={TimelineIcon}>Recent Trades</SectionHeader>
                    <Surface sx={{ mb: 4, p: 0 }}>
                        <Box sx={{
                            display: 'flex', px: 2.5, py: 1,
                            borderBottom: t => `1px solid ${t.palette.custom.border.subtle}`,
                            bgcolor: 'custom.surface.subtle',
                        }}>
                            <Typography variant="overline" sx={{ color: 'text.muted', flex: '0 0 100px' }}>Date</Typography>
                            <Typography variant="overline" sx={{ color: 'text.muted', flex: '0 0 60px' }}>Type</Typography>
                            <Typography variant="overline" sx={{ color: 'text.muted', flex: '0 0 80px' }}>Ticker</Typography>
                            <Typography variant="overline" sx={{ color: 'text.muted', flex: 1 }}>Amount</Typography>
                            <Typography variant="overline" sx={{ color: 'text.muted', flex: '0 0 90px', textAlign: 'right' }}>1yr Return</Typography>
                        </Box>
                        {[...tradesData]
                            .filter(t => t.asset_code === 'ST' && t.ticker)
                            .sort((a, b) => (b.transaction_date || '').localeCompare(a.transaction_date || ''))
                            .slice(0, 10)
                            .map((t, i) => {
                                const roi = t.stock_roi_1yr ? ((t.stock_roi_1yr - 1) * 100) : null;
                                const isBuy = t.transaction === 'purchase';
                                const amount = t.asset_value_low && t.asset_value_high
                                    ? `$${(t.asset_value_low / 1000).toFixed(0)}K–$${(t.asset_value_high / 1000).toFixed(0)}K`
                                    : '—';
                                return (
                                    <Box key={i} sx={{
                                        display: 'flex', alignItems: 'center', px: 2.5, py: 1.25,
                                        borderBottom: i < 9 ? th => `1px solid ${th.palette.custom.border.subtle}` : 'none',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                                    }}>
                                        <Typography variant="body2" sx={{
                                            color: 'text.muted', flex: '0 0 100px',
                                            fontFamily: th => th.typography.fontFamilyMono, fontSize: '0.8125rem',
                                        }}>
                                            {t.transaction_date}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            flex: '0 0 60px', fontWeight: 600, fontSize: '0.75rem',
                                            color: isBuy ? 'custom.direction.buyFg' : 'custom.direction.sellFg',
                                        }}>
                                            {isBuy ? 'BUY' : 'SELL'}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            component={Link}
                                            to={`/issuers/${encodeURIComponent(t.ticker)}`}
                                            sx={{
                                                flex: '0 0 80px',
                                                fontFamily: th => th.typography.fontFamilyMono,
                                                fontWeight: 600, color: 'text.primary',
                                                textDecoration: 'none',
                                                '&:hover': { color: 'primary.light' },
                                            }}
                                        >
                                            {t.ticker}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.muted', flex: 1 }}>
                                            {amount}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            flex: '0 0 90px', textAlign: 'right',
                                            fontFamily: th => th.typography.fontFamilyMono,
                                            fontWeight: 600,
                                            color: roi == null ? 'text.muted'
                                                : roi >= 0 ? 'success.main' : 'error.main',
                                        }}>
                                            {roi != null ? `${roi > 0 ? '+' : ''}${roi.toFixed(1)}%` : '—'}
                                        </Typography>
                                    </Box>
                                );
                            })}
                    </Surface>
                </>
            )}

            {/* ── Best Trades (Highlights) ── */}
            {(() => {
                const bestTrades = [...tradesData]
                    .filter(t => t.asset_code === 'ST' && t.ticker && t.stock_roi_1yr && t.transaction === 'purchase')
                    .sort((a, b) => (b.stock_roi_1yr || 0) - (a.stock_roi_1yr || 0))
                    .slice(0, 5);
                if (bestTrades.length === 0) return null;
                return (
                    <>
                        <SectionHeader icon={ShowChartIcon}>Best Trades</SectionHeader>
                        <Surface sx={{ mb: 4, p: 0 }}>
                            <Box sx={{
                                display: 'flex', px: 2.5, py: 1,
                                borderBottom: t => `1px solid ${t.palette.custom.border.subtle}`,
                                bgcolor: 'custom.surface.subtle',
                            }}>
                                <Typography variant="overline" sx={{ color: 'text.muted', flex: '0 0 100px' }}>Date</Typography>
                                <Typography variant="overline" sx={{ color: 'text.muted', flex: '0 0 80px' }}>Ticker</Typography>
                                <Typography variant="overline" sx={{ color: 'text.muted', flex: 1 }}>Sector</Typography>
                                <Typography variant="overline" sx={{ color: 'text.muted', flex: '0 0 90px', textAlign: 'right' }}>Amount</Typography>
                                <Typography variant="overline" sx={{ color: 'text.muted', flex: '0 0 90px', textAlign: 'right' }}>1yr Return</Typography>
                                <Typography variant="overline" sx={{ color: 'text.muted', flex: '0 0 90px', textAlign: 'right' }}>Alpha</Typography>
                            </Box>
                            {bestTrades.map((t, i) => {
                                const roi = ((t.stock_roi_1yr - 1) * 100);
                                const tradeAlpha = t.alpha ? (t.alpha * 100) : null;
                                const amount = t.asset_value_low && t.asset_value_high
                                    ? `$${(t.asset_value_low / 1000).toFixed(0)}K–$${(t.asset_value_high / 1000).toFixed(0)}K`
                                    : '—';
                                return (
                                    <Box key={i} sx={{
                                        display: 'flex', alignItems: 'center', px: 2.5, py: 1.25,
                                        borderBottom: i < bestTrades.length - 1
                                            ? th => `1px solid ${th.palette.custom.border.subtle}` : 'none',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                                    }}>
                                        <Typography variant="body2" sx={{
                                            color: 'text.muted', flex: '0 0 100px',
                                            fontFamily: th => th.typography.fontFamilyMono, fontSize: '0.8125rem',
                                        }}>
                                            {t.transaction_date}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            component={Link}
                                            to={`/issuers/${encodeURIComponent(t.ticker)}`}
                                            sx={{
                                                flex: '0 0 80px',
                                                fontFamily: th => th.typography.fontFamilyMono,
                                                fontWeight: 600, color: 'text.primary',
                                                textDecoration: 'none',
                                                '&:hover': { color: 'primary.light' },
                                            }}
                                        >
                                            {t.ticker}
                                        </Typography>
                                        <Typography variant="caption" sx={{
                                            color: 'text.muted', flex: 1,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }}>
                                            {t.sector || '—'}
                                        </Typography>
                                        <Typography variant="caption" sx={{
                                            color: 'text.muted', flex: '0 0 90px', textAlign: 'right',
                                        }}>
                                            {amount}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            flex: '0 0 90px', textAlign: 'right',
                                            fontFamily: th => th.typography.fontFamilyMono,
                                            fontWeight: 700, color: 'success.main',
                                        }}>
                                            +{roi.toFixed(1)}%
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            flex: '0 0 90px', textAlign: 'right',
                                            fontFamily: th => th.typography.fontFamilyMono,
                                            fontWeight: 600,
                                            color: tradeAlpha != null && tradeAlpha >= 0 ? 'success.main' : tradeAlpha != null ? 'error.main' : 'text.muted',
                                        }}>
                                            {tradeAlpha != null ? `${tradeAlpha > 0 ? '+' : ''}${tradeAlpha.toFixed(1)}%` : '—'}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Surface>
                    </>
                );
            })()}

            {/* ── Sales & Bearish ── */}
            {bearish_transactions?.count > 0 && (
                <>
                    <SectionHeader icon={TrendingDownIcon}>Sales &amp; Bearish</SectionHeader>
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={4}>
                            <StatCard label="Count" value={bearish_transactions.count.toLocaleString()} mono />
                        </Grid>
                        {bearishRoiPercent !== null && (
                            <Grid item xs={4}>
                                <StatCard label="ROI" value={`${bearishRoiPercent}%`} mono />
                            </Grid>
                        )}
                        {bearish_transactions.accuracy != null && (
                            <Grid item xs={4}>
                                <StatCard label="Accuracy" value={`${(bearish_transactions.accuracy * 100).toFixed(1)}%`} mono />
                            </Grid>
                        )}
                    </Grid>
                </>
            )}

            {/* ── Terms in Office (compact horizontal) ── */}
            {groupedTerms.length > 0 && (
                <Surface sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <WorkHistoryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="overline" sx={{ color: 'text.secondary', mr: 1 }}>
                            Terms in Office
                        </Typography>
                        {[...groupedTerms].reverse().map((group, i) => {
                            const isCurrent = group.endYear == null;
                            const endValue = isCurrent ? new Date().getFullYear() : group.endYear;
                            const years = Math.max(0, endValue - (group.startYear || endValue));
                            return (
                                <Chip
                                    key={i}
                                    label={`${group.memberType || 'Member'} · ${group.startYear}–${isCurrent ? 'Present' : group.endYear} · ${years}yr${years !== 1 ? 's' : ''}`}
                                    size="small"
                                    sx={{
                                        bgcolor: isCurrent ? 'custom.status.activeBg' : 'rgba(255,255,255,0.04)',
                                        color: isCurrent ? 'custom.status.active' : 'text.secondary',
                                        border: isCurrent
                                            ? t => `1px solid ${t.palette.custom.status.active}40`
                                            : t => `1px solid ${t.palette.custom.border.subtle}`,
                                        fontFamily: t => t.typography.fontFamilyMono,
                                        fontSize: '0.75rem',
                                    }}
                                />
                            );
                        })}
                    </Stack>
                </Surface>
            )}

            {/* ── Legislation ── */}
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Surface sx={{ height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <GavelIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                                Legislation
                            </Typography>
                        </Box>

                        <Grid container spacing={2} sx={{ mb: 2.5 }}>
                            <Grid item xs={6}>
                                <Box sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    bgcolor: 'custom.surface.subtle',
                                    border: theme => `1px solid ${theme.palette.custom.border.subtle}`,
                                }}>
                                    <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                        Sponsored
                                    </Typography>
                                    <Typography sx={{
                                        color: 'text.primary',
                                        fontWeight: 600,
                                        fontSize: '1.25rem',
                                        fontFamily: theme => theme.typography.fontFamilyMono,
                                    }}>
                                        {sponsoredLegislation.count?.toLocaleString() ?? '—'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    bgcolor: 'custom.surface.subtle',
                                    border: theme => `1px solid ${theme.palette.custom.border.subtle}`,
                                }}>
                                    <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                        Cosponsored
                                    </Typography>
                                    <Typography sx={{
                                        color: 'text.primary',
                                        fontWeight: 600,
                                        fontSize: '1.25rem',
                                        fontFamily: theme => theme.typography.fontFamilyMono,
                                    }}>
                                        {cosponsoredLegislation.count?.toLocaleString() ?? '—'}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {visibleLegislation.length > 0 ? (
                            <>
                                {visibleLegislation.map((bill, index) => {
                                    const billBrowseUrl = publicCongressBillUrl(bill);
                                    return (
                                    <Box key={index} mb={2.5}>
                                        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, mb: 0.75, lineHeight: 1.45 }}>
                                            {bill.title}
                                        </Typography>
                                        <Box display="flex" gap={0.75} flexWrap="wrap" mb={0.75}>
                                            {bill.type && (
                                                <Chip label={bill.type} size="small" sx={{ height: 18, fontSize: '0.68rem' }} />
                                            )}
                                            {bill.policyArea?.name && (
                                                <Chip label={bill.policyArea.name} size="small" sx={{ height: 18, fontSize: '0.68rem' }} />
                                            )}
                                            {bill.congress && (
                                                <Chip label={`${bill.congress}th Congress`} size="small" sx={{ height: 18, fontSize: '0.68rem' }} />
                                            )}
                                        </Box>
                                        <Typography variant="caption" sx={{ color: 'text.muted', display: 'block', mb: 0.25 }}>
                                            Introduced: {new Date(bill.introducedDate).toLocaleDateString()}
                                        </Typography>
                                        {bill.latestAction && (
                                            <Typography variant="caption" sx={{ color: 'text.muted', display: 'block', mb: 0.5 }}>
                                                Latest: {bill.latestAction.text} ({new Date(bill.latestAction.actionDate).toLocaleDateString()})
                                            </Typography>
                                        )}
                                        {billBrowseUrl && (
                                            <MuiLink
                                                href={billBrowseUrl}
                                                target="_blank"
                                                rel="noopener"
                                                sx={{ fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                            >
                                                View Bill <OpenInNewIcon sx={{ fontSize: 11 }} />
                                            </MuiLink>
                                        )}
                                        {index < visibleLegislation.length - 1 && (
                                            <Box sx={{ mt: 2, borderBottom: theme => `1px solid ${theme.palette.custom.border.subtle}` }} />
                                        )}
                                    </Box>
                                    );
                                })}
                                {legislationData.length > LEGISLATION_PAGE_SIZE && (
                                    <Button
                                        onClick={() => setShowAllLegislation(prev => !prev)}
                                        sx={{ mt: 0.5 }}
                                        variant="text"
                                    >
                                        {showAllLegislation
                                            ? 'Show less'
                                            : `Show ${legislationData.length - LEGISLATION_PAGE_SIZE} more`}
                                    </Button>
                                )}
                            </>
                        ) : (
                            <Typography variant="body2" sx={{ color: 'text.muted' }}>
                                No recent legislation data available.
                            </Typography>
                        )}
                    </Surface>
                </Grid>
            </Grid>
        </Container>
    );
}

export default CongressMemberProfile;
